import { birthdayDateInYear } from './birthdays';

export interface LeaveKindFieldDef {
  key: string;
  label: string;
  type: 'text' | 'textarea';
  required: boolean;
  placeholder?: string;
}

export const LEAVE_KINDS = [
  {
    key: 'vacation',
    label: 'Vacaciones',
    emoji: '🏖️',
    countsVacation: true,
    description: 'Vacaciones LFT — descuentan de la prestación anual.',
    lftRef: 'Artículos 76, 78, 80 y 81 LFT',
    declaration:
      'Solicito vacaciones a las que tengo derecho conforme a los artículos 76, 78 y 81 ' +
      'de la Ley Federal del Trabajo, las cuales deberé disfrutar de manera continua. ' +
      'Reconozco que el patrón cubrirá la prima vacacional correspondiente (artículo 80 LFT).',
    extraFields: [] as LeaveKindFieldDef[],
  },
  {
    key: 'paid_leave',
    label: 'Permiso con goce',
    emoji: '🛫',
    countsVacation: false,
    description: 'Días fuera autorizados con sueldo, no descuentan vacaciones.',
    lftRef: 'Artículo 132 fracción IX y similares de la LFT',
    declaration:
      'Solicito permiso con goce de sueldo. El patrón conserva la facultad de autorizarlo ' +
      'conforme a las políticas internas y a la Ley Federal del Trabajo.',
    extraFields: [] as LeaveKindFieldDef[],
  },
  {
    key: 'unpaid_leave',
    label: 'Permiso sin goce',
    emoji: '🚪',
    countsVacation: false,
    description: 'Días fuera sin sueldo.',
    lftRef: 'Artículos 42 y 132 LFT',
    declaration:
      'Solicito permiso sin goce de sueldo y manifiesto mi conformidad expresa con que ' +
      'los días autorizados no sean remunerados ni computables para antigüedad.',
    extraFields: [] as LeaveKindFieldDef[],
  },
  {
    key: 'sick',
    label: 'Incapacidad / enfermedad',
    emoji: '🤒',
    countsVacation: false,
    description: 'Incapacidad médica.',
    lftRef: 'Artículos 42 fracción II, 134 fracción II LFT y normatividad del IMSS',
    declaration:
      'Manifiesto que el periodo solicitado corresponde a una incapacidad médica acreditada ' +
      'por el IMSS o por médico autorizado. Adjunto el folio de la incapacidad.',
    extraFields: [
      { key: 'imss_folio', label: 'Folio de incapacidad IMSS (ST-1/ST-2/ST-3)', type: 'text', required: true, placeholder: 'Ej. 1234567890' },
      { key: 'doctor', label: 'Médico o institución (opcional)', type: 'text', required: false },
    ] as LeaveKindFieldDef[],
  },
  {
    key: 'paternity',
    label: 'Paternidad / maternidad',
    emoji: '👶',
    countsVacation: false,
    description: 'Permiso por paternidad o maternidad.',
    lftRef: 'Artículos 132 fr. XXVII Bis y 170 LFT',
    declaration:
      'Solicito el permiso por nacimiento o adopción al que tengo derecho conforme al ' +
      'artículo 132 fracción XXVII Bis (paternidad) o 170 LFT (maternidad).',
    extraFields: [
      { key: 'event_type', label: 'Evento (nacimiento / adopción)', type: 'text', required: true, placeholder: 'Nacimiento' },
      { key: 'event_date', label: 'Fecha del evento', type: 'text', required: true, placeholder: 'YYYY-MM-DD' },
      { key: 'cert_reference', label: 'Acta o constancia (folio, opcional)', type: 'text', required: false },
    ] as LeaveKindFieldDef[],
  },
  {
    key: 'personal',
    label: 'Asuntos personales',
    emoji: '📌',
    countsVacation: false,
    description: 'Permiso por asuntos personales (prestación contractual o de RIT).',
    lftRef: 'Conforme a contrato individual o Reglamento Interior de Trabajo',
    declaration:
      'Solicito un permiso por asuntos personales al amparo del contrato individual o del ' +
      'Reglamento Interior de Trabajo aplicable.',
    extraFields: [] as LeaveKindFieldDef[],
  },
  {
    key: 'birthday',
    label: 'Día de cumpleaños',
    emoji: '🎂',
    countsVacation: false,
    description: 'Día libre por cumpleaños: solo puede solicitarse para la fecha de cumpleaños, una vez al año.',
    lftRef: 'Prestación superior a la ley conforme al contrato individual o Reglamento Interior de Trabajo',
    declaration:
      'Solicito el día libre por mi cumpleaños al amparo del contrato individual o del ' +
      'Reglamento Interior de Trabajo aplicable. Reconozco que corresponde exclusivamente ' +
      'a la fecha de mi cumpleaños, que se disfruta una sola vez por año y que no es ' +
      'acumulable ni canjeable por otra fecha.',
    extraFields: [] as LeaveKindFieldDef[],
  },
  {
    key: 'brief',
    label: 'Ausencia Breve',
    emoji: '🕐',
    countsVacation: false,
    description: 'Ausencia temporal de hasta 4 horas en un solo día (mañana o tarde).',
    lftRef: 'Permiso temporal conforme al contrato individual o Reglamento Interior de Trabajo',
    declaration:
      'Solicito una ausencia breve de hasta cuatro horas (un solo turno) en la fecha y ' +
      'horario indicados, al amparo del contrato individual o del Reglamento Interior de ' +
      'Trabajo aplicable. Reconozco que estos eventos no son acumulables y se renuevan cada año.',
    extraFields: [] as LeaveKindFieldDef[],
  },
] as const;

export type LeaveKind = typeof LEAVE_KINDS[number]['key'];
export const LEAVE_KIND_KEYS: LeaveKind[] = LEAVE_KINDS.map(k => k.key);

export function leaveKindMeta(key: string) {
  return LEAVE_KINDS.find(k => k.key === key) ?? {
    key,
    label: key,
    emoji: '📅',
    countsVacation: false,
    description: '',
    lftRef: '',
    declaration: '',
    extraFields: [] as LeaveKindFieldDef[],
  };
}

export const LEAVE_STATUSES = [
  { key: 'pending',   label: 'Pendiente',   color: 'yellow' as const, emoji: '⏳' },
  { key: 'approved',  label: 'Autorizada',  color: 'green'  as const, emoji: '✅' },
  { key: 'rejected',  label: 'Rechazada',   color: 'red'    as const, emoji: '❌' },
  { key: 'cancelled', label: 'Cancelada',   color: 'gray'   as const, emoji: '🚫' },
];

export type LeaveStatus = 'pending' | 'approved' | 'rejected' | 'cancelled';
export const LEAVE_STATUS_KEYS: LeaveStatus[] = LEAVE_STATUSES.map(s => s.key as LeaveStatus);

export function leaveStatusLabel(key: string): string {
  return LEAVE_STATUSES.find(s => s.key === key)?.label || key;
}
export function leaveStatusMeta(key: string) {
  return LEAVE_STATUSES.find(s => s.key === key) ?? { key, label: key, color: 'gray' as const, emoji: '•' };
}

/**
 * Días de vacaciones a los que tiene derecho un empleado según LFT
 * (reforma 2023). Antes de cumplir 1 año = 0. Año 1 cumplido = 12; +2 cada
 * año hasta llegar a 20 al cumplir 5. Después +2 cada 5 años: 22 a partir
 * del año 6, 24 a partir del año 11, 26 a partir del año 16, …
 */
export function entitledVacationDays(hireDate: Date, asOf: Date = new Date()): number {
  let years = asOf.getFullYear() - hireDate.getFullYear();
  if (asOf.getMonth() < hireDate.getMonth() ||
      (asOf.getMonth() === hireDate.getMonth() && asOf.getDate() < hireDate.getDate())) {
    years -= 1;
  }
  if (years < 1) return 0;
  if (years <= 5) return 12 + 2 * (years - 1);
  const tiers = Math.floor((years - 6) / 5) + 1;
  return 20 + 2 * tiers;
}

export function yearsOfService(hireDate: Date, asOf: Date = new Date()): number {
  let years = asOf.getFullYear() - hireDate.getFullYear();
  if (asOf.getMonth() < hireDate.getMonth() ||
      (asOf.getMonth() === hireDate.getMonth() && asOf.getDate() < hireDate.getDate())) {
    years -= 1;
  }
  return Math.max(0, years);
}

/** Returns inclusive number of calendar days between start and end (>= 1). */
export function inclusiveDayCount(start: Date, end: Date): number {
  if (end < start) return 0;
  const s = Date.UTC(start.getFullYear(), start.getMonth(), start.getDate());
  const e = Date.UTC(end.getFullYear(), end.getMonth(), end.getDate());
  return Math.round((e - s) / (24 * 60 * 60 * 1000)) + 1;
}

export const DEFAULT_NON_WORKING_WEEKDAYS: ReadonlySet<number> = new Set([0, 6]);

/**
 * Días hábiles entre start y end (inclusive): excluye fines de semana y los
 * días festivos dados de alta por administración (Set de fechas YMD locales).
 * Es la fuente de verdad del lado cliente; el servidor cuenta lo mismo con
 * `countWorkingDays`/`isWorkingDay` de daily-utils, alimentados por el mismo
 * catálogo (`daily_non_working_weekdays`, `daily_holidays`).
 */
export function workingDayCount(
  start: Date,
  end: Date,
  holidays: ReadonlySet<string> = new Set(),
  nonWorkingWeekdays: ReadonlySet<number> = DEFAULT_NON_WORKING_WEEKDAYS,
): number {
  if (end < start) return 0;
  let count = 0;
  const cursor = new Date(start.getFullYear(), start.getMonth(), start.getDate());
  const last = new Date(end.getFullYear(), end.getMonth(), end.getDate());
  while (cursor.getTime() <= last.getTime()) {
    if (!nonWorkingWeekdays.has(cursor.getDay()) && !holidays.has(ymdLocal(cursor))) count++;
    cursor.setDate(cursor.getDate() + 1);
  }
  return count;
}

/**
 * Primer día hábil en o después de `ymd` (el mismo día si ya es hábil). Usa el
 * mismo catálogo que `workingDayCount`, por lo que servidor y cliente resuelven
 * la misma fecha.
 */
export function observedWorkingDayYmd(
  ymd: string,
  holidays: ReadonlySet<string> = new Set(),
  nonWorkingWeekdays: ReadonlySet<number> = DEFAULT_NON_WORKING_WEEKDAYS,
): string {
  const cursor = parseYmdLocal(ymd);
  while (nonWorkingWeekdays.has(cursor.getDay()) || holidays.has(ymdLocal(cursor))) {
    cursor.setDate(cursor.getDate() + 1);
  }
  return ymdLocal(cursor);
}

export interface BirthdayLeaveDate {
  /** Fecha real del cumpleaños en ese año. */
  natural: string;
  /** Día que se solicita: se recorre al siguiente hábil si el cumpleaños cae en fin de semana o festivo. */
  observed: string;
  shifted: boolean;
}

export function birthdayLeaveDate(
  birthYmd: string,
  year: number,
  holidays?: ReadonlySet<string>,
  nonWorkingWeekdays?: ReadonlySet<number>,
): BirthdayLeaveDate {
  const natural = birthdayDateInYear(birthYmd, year);
  const observed = observedWorkingDayYmd(natural, holidays, nonWorkingWeekdays);
  return { natural, observed, shifted: observed !== natural };
}

export function ymdLocal(d: Date): string {
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
}

export function parseYmdLocal(s: string): Date {
  const [y, m, d] = s.split('-').map(Number);
  return new Date(y, (m || 1) - 1, d || 1);
}

export function isYmd(s: unknown): s is string {
  return typeof s === 'string' && /^\d{4}-\d{2}-\d{2}$/.test(s);
}

export interface LeaveRange {
  start_date: string; // YYYY-MM-DD
  end_date: string;
  kind: string;
}

/**
 * Returns the leave range overlapping the given date (local), or null.
 * Dates are compared as YMD strings to avoid TZ surprises.
 */
export function leaveForDate(date: Date, leaves: LeaveRange[]): LeaveRange | null {
  const target = ymdLocal(date);
  for (const l of leaves) {
    const s = String(l.start_date).slice(0, 10);
    const e = String(l.end_date).slice(0, 10);
    if (target >= s && target <= e) return l;
  }
  return null;
}

// --- Ausencia Breve (ausencias temporales ≤ 4 h) ---

export const BRIEF_PERIODS = [
  { key: 'morning',   label: 'Mañana', start: '09:00', end: '13:00' },
  { key: 'afternoon', label: 'Tarde',  start: '14:00', end: '18:00' },
] as const;

export type BriefPeriod = typeof BRIEF_PERIODS[number]['key'];
export const BRIEF_PERIOD_KEYS: BriefPeriod[] = BRIEF_PERIODS.map(p => p.key);
export const BRIEF_HOURS_PER_EVENT = 4;
export const BRIEF_DEFAULT_MAX_EVENTS = 4;

export function isBriefKind(kind: string): boolean {
  return kind === 'brief';
}

export function isBirthdayKind(kind: string): boolean {
  return kind === 'birthday';
}

export function briefPeriodMeta(key: string) {
  return BRIEF_PERIODS.find(p => p.key === key) ?? null;
}

/** "Mañana · 09:00–13:00" a partir del kind_data de una ausencia breve. */
export function briefSummaryLabel(kindData: unknown): string {
  if (!kindData || typeof kindData !== 'object') return '';
  const kd = kindData as Record<string, unknown>;
  const meta = briefPeriodMeta(String(kd.period ?? ''));
  if (!meta) return '';
  return `${meta.label} · ${meta.start}–${meta.end}`;
}
